import { useState, useEffect } from "react";
import { Search, FileDown, Download, Smartphone, Shield } from "lucide-react";
import api from "@/lib/api";

const CATEGORIES = ["All", "Primary TET", "Upper Primary TET", "C-TET", "Group C", "Group D", "SSC", "Banking", "General"];

export default function EbooksPage() {
  const [ebooks, setEbooks] = useState([]);
  const [category, setCategory] = useState("All");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchEbooks(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  const fetchEbooks = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (category !== "All") params.append("category", category);
      const { data } = await api.get(`/ebooks?${params.toString()}`);
      setEbooks(data);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const filtered = search ? ebooks.filter(b => b.title.toLowerCase().includes(search.toLowerCase())) : ebooks;

  return (
    <div className="page-with-nav bg-white min-h-screen" data-testid="ebooks-page">
      {/* Header */}
      <div className="bg-[#7C3AED] px-4 pt-4 pb-5 text-white">
        <div className="flex items-center gap-2 mb-1">
          <FileDown size={20} />
          <h1 className="text-xl font-black" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            E-Books
          </h1>
        </div>
        <p className="text-xs text-white/80">Soft copy PDF books - Pay, download & read instantly</p>
        <div className="relative mt-3">
          <Search className="absolute left-3 top-3 text-purple-300" size={16} />
          <input
            data-testid="ebooks-search"
            type="text"
            placeholder="Search e-books..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-purple-700 border-b-2 border-purple-500 pl-9 pr-4 py-2.5 text-sm text-white placeholder-purple-300 focus:outline-none focus:border-white transition-none"
          />
        </div>
      </div>

      {/* Categories */}
      <div className="bg-purple-50 px-4 py-3">
        <div className="flex overflow-x-auto gap-2 scrollbar-hide">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setCategory(cat)}
              className={`flex-shrink-0 text-xs font-bold px-3 py-2 transition-none ${
                category === cat ? "bg-[#7C3AED] text-white" : "bg-white text-slate-600 border border-slate-200"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Features badge */}
      <div className="px-4 py-2 bg-green-50 flex items-center gap-4">
        <div className="flex items-center gap-1.5">
          <Download size={12} className="text-green-600" />
          <span className="text-[10px] font-bold text-green-700">Instant Download</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Smartphone size={12} className="text-green-600" />
          <span className="text-[10px] font-bold text-green-700">Read on Any Device</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Shield size={12} className="text-green-600" />
          <span className="text-[10px] font-bold text-green-700">Printable</span>
        </div>
      </div>

      {/* E-Books List */}
      <div className="px-4 py-4">
        <p className="text-xs text-slate-500 mb-3">{filtered.length} e-books available</p>
        {loading ? (
          <div className="text-center py-12 text-slate-400">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12">
            <FileDown size={48} className="mx-auto text-slate-300 mb-3" />
            <p className="text-sm text-slate-400">No e-books found</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="ebooks-list">
            {filtered.map((ebook) => {
              const discount = ebook.mrp > ebook.price ? Math.round(((ebook.mrp - ebook.price) / ebook.mrp) * 100) : 0;
              return (
                <div key={ebook.id} data-testid={`ebook-${ebook.id}`} className="bg-white border border-slate-100">
                  <div className="flex gap-3 p-3">
                    {/* Cover */}
                    <div className="w-24 aspect-[3/4] bg-slate-100 flex-shrink-0 overflow-hidden">
                      {ebook.image_url ? (
                        <img src={ebook.image_url} alt={ebook.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-[#7C3AED]/10 flex items-center justify-center">
                          <FileDown size={28} className="text-[#7C3AED]" />
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 flex flex-col justify-between py-1">
                      <div>
                        <span className="text-[10px] font-bold uppercase text-[#7C3AED]">{ebook.category}</span>
                        <h3 className="text-sm font-bold text-slate-900 mt-0.5 line-clamp-2">{ebook.title}</h3>
                        {ebook.author && <p className="text-xs text-slate-500 mt-0.5">by {ebook.author}</p>}
                        <div className="flex items-center gap-2 mt-1">
                          {ebook.file_format && (
                            <span className="bg-[#7C3AED]/10 text-[#7C3AED] text-[10px] font-bold px-1.5 py-0.5">{ebook.file_format}</span>
                          )}
                          {ebook.pages && <span className="text-[10px] text-slate-400">{ebook.pages}</span>}
                          {ebook.file_size && <span className="text-[10px] text-slate-400">{ebook.file_size}</span>}
                        </div>
                      </div>

                      {/* Price */}
                      <div className="mt-2">
                        <div className="flex items-center gap-2">
                          <span className="font-black text-lg text-slate-900">&#8377;{ebook.price}</span>
                          {discount > 0 && (
                            <>
                              <span className="text-xs text-slate-400 line-through">&#8377;{ebook.mrp}</span>
                              <span className="bg-green-600 text-white text-[10px] font-bold px-1.5 py-0.5">{discount}% OFF</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {ebook.description && (
                    <p className="px-3 pb-2 text-xs text-slate-500 line-clamp-2">{ebook.description}</p>
                  )}

                  {/* Pay & Download Button */}
                  <div className="px-3 pb-3">
                    {ebook.razorpay_url ? (
                      <a
                        href={ebook.razorpay_url}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`pay-download-${ebook.id}`}
                        className="w-full bg-[#7C3AED] text-white font-bold py-3.5 text-sm flex items-center justify-center gap-2 transition-none hover:bg-[#6D28D9] no-underline block text-center"
                      >
                        <Download size={16} /> Pay & Download Now - &#8377;{ebook.price}
                      </a>
                    ) : (
                      <div className="w-full bg-slate-200 text-slate-500 font-bold py-3.5 text-sm flex items-center justify-center gap-2">
                        <Download size={16} /> Coming Soon
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
